import React, { useState } from 'react';
import { 
  X, 
  TrendingUp, 
  TrendingDown, 
  Eye, 
  Users, 
  Target,
  Lightbulb,
  FileText,
  Play,
  BarChart3,
  DollarSign,
  Clock,
  Star,
  Award,
  Calendar,
  Zap,
  CheckCircle,
  XCircle,
  MessageSquare,
  ThumbsUp,
  Video,
  TrendingUpIcon
} from 'lucide-react';
import './TopCreatorsModal.css';

const TopCreatorsModal = ({ creator, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Mock data specific to this creator (replace with real data)
  const creatorStats = {
    name: creator.name,
    handle: creator.handle,
    avatar: creator.avatar,
    rank: 1,
    totalViews: creator.totalViews,
    totalEngagement: Math.round(creator.totalViews * 0.048), // 4.8% engagement rate
    avgCTR: creator.avgCTR,
    avgConversion: 3.2,
    totalRevenue: Math.round(creator.totalViews * 3.2 * 0.01 * 15), // Estimated revenue
    contentCount: creator.contentCount,
    successRate: creator.successRate,
    avgRating: 4.7,
    avgWatchTime: 52,
    completionRate: 88,
    trend: '+23%',
    joinDate: '2024-01-15',
    totalVideos: creator.contentCount,
    platforms: creator.platforms,
    topPerformingContent: [
      {
        title: "Problem-Solution Hook",
        brief: "Holiday Gift Guide",
        views: 180000,
        ctr: 7.2,
        conversion: 4.1,
        rating: 4.8,
        date: "2024-01-20"
      },
      {
        title: "Story Hook",
        brief: "Summer Product Launch",
        views: 145000,
        ctr: 6.8,
        conversion: 3.9,
        rating: 4.6,
        date: "2024-01-18"
      },
      {
        title: "Question Hook",
        brief: "Fitness Challenge",
        views: 120000,
        ctr: 6.1,
        conversion: 3.5,
        rating: 4.4,
        date: "2024-01-15"
      }
    ],
    recentBriefs: [
      {
        title: "Holiday Gift Guide",
        status: "completed",
        performance: "excellent",
        views: 180000,
        revenue: 5600,
        rating: 4.8
      },
      {
        title: "Summer Product Launch",
        status: "completed",
        performance: "good",
        views: 145000,
        revenue: 4200,
        rating: 4.6
      },
      {
        title: "Fitness Challenge",
        status: "in_progress",
        performance: "pending",
        views: 120000,
        revenue: 3800,
        rating: 4.4
      }
    ],
    creatorStrengths: [
      "Problem-solution hooks",
      "High engagement rates",
      "Consistent quality",
      "Quick turnaround"
    ],
    improvementAreas: [
      "Video length optimization",
      "CTA placement",
      "Brand integration"
    ]
  };

  const creatorInsights = [
    {
      type: "positive",
      title: "Exceptional Hook Performance",
      insight: `${creatorStats.name}'s hooks consistently outperform platform averages by 35%`,
      recommendation: "Consider featuring their hook techniques in creator training"
    },
    {
      type: "improvement",
      title: "Content Length Optimization",
      insight: "Videos under 30 seconds have 15% higher completion rates for this creator",
      recommendation: "Encourage shorter, more focused content creation"
    },
    {
      type: "warning",
      title: "Performance Consistency",
      insight: "Performance varies significantly between brief types (180K vs 120K views)",
      recommendation: "Focus on briefs that match their content style"
    }
  ];

  return (
    <div className="top-creators-modal-overlay" onClick={onClose}>
      <div className="top-creators-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="top-creators-modal-header">
          <div className="top-creators-header-content">
            <div className="top-creators-title-section">
              <div className="top-creators-avatar-section">
                <div className="top-creators-avatar">
                  <img 
                    src={creatorStats.avatar} 
                    alt={creatorStats.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="top-creators-avatar-fallback">
                    {creatorStats.name.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
                <div className="top-creators-info">
                  <h2>{creatorStats.name}</h2>
                  <p className="creator-handle">{creatorStats.handle}</p>
                  <div className="top-creators-rank-badge">#{creatorStats.rank} Top Creator</div>
                </div>
              </div>
            </div>
          </div>
          <button className="top-creators-modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="top-creators-modal-tabs">
          <button 
            className={`top-creators-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <BarChart3 size={16} />
            Creator Overview
          </button>
          <button 
            className={`top-creators-tab-btn ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            <Video size={16} />
            Content Performance
          </button>
          <button 
            className={`top-creators-tab-btn ${activeTab === 'insights' ? 'active' : ''}`}
            onClick={() => setActiveTab('insights')}
          >
            <Lightbulb size={16} />
            Creator Insights
          </button>
        </div>

        <div className="top-creators-modal-content">
          {activeTab === 'overview' && (
            <div className="top-creators-overview-tab">
              <div className="top-creators-metrics-section">
                <h3>Creator Performance Metrics</h3>
                <div className="top-creators-metrics-grid">
                  <div className="top-creators-metric-card">
                    <div className="top-creators-metric-icon">
                      <Eye size={18} />
                    </div>
                    <div className="top-creators-metric-content">
                      <span className="top-creators-metric-value">{(creatorStats.totalViews / 1000).toFixed(0)}K</span>
                      <span className="top-creators-metric-label">Total Views</span>
                    </div>
                  </div>
                  <div className="top-creators-metric-card">
                    <div className="top-creators-metric-icon">
                      <Target size={18} />
                    </div>
                    <div className="top-creators-metric-content">
                      <span className="top-creators-metric-value">{creatorStats.avgCTR}%</span>
                      <span className="top-creators-metric-label">Avg CTR</span>
                    </div>
                  </div>
                  <div className="top-creators-metric-card">
                    <div className="top-creators-metric-icon">
                      <TrendingUp size={18} />
                    </div>
                    <div className="top-creators-metric-content">
                      <span className="top-creators-metric-value">{creatorStats.avgConversion}%</span>
                      <span className="top-creators-metric-label">Conversion</span>
                    </div>
                  </div>
                  <div className="top-creators-metric-card">
                    <div className="top-creators-metric-icon">
                      <DollarSign size={18} />
                    </div>
                    <div className="top-creators-metric-content">
                      <span className="top-creators-metric-value">${(creatorStats.totalRevenue / 1000).toFixed(1)}K</span>
                      <span className="top-creators-metric-label">Revenue</span>
                    </div>
                  </div>
                  <div className="top-creators-metric-card">
                    <div className="top-creators-metric-icon">
                      <Award size={18} />
                    </div>
                    <div className="top-creators-metric-content">
                      <span className="top-creators-metric-value">{creatorStats.successRate}%</span>
                      <span className="top-creators-metric-label">Success Rate</span>
                    </div>
                  </div>
                  <div className="top-creators-metric-card">
                    <div className="top-creators-metric-icon">
                      <Star size={18} />
                    </div>
                    <div className="top-creators-metric-content">
                      <span className="top-creators-metric-value">{creatorStats.avgRating}</span>
                      <span className="top-creators-metric-label">Avg Rating</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="top-creators-summary-section">
                <h3>Creator Summary</h3>
                <div className="top-creators-summary-card">
                  <p><strong>{creatorStats.name}</strong> is a top-performing creator with <strong>{creatorStats.successRate}% success rate</strong>. They've created <strong>{creatorStats.contentCount} videos</strong> generating <strong>{(creatorStats.totalViews / 1000000).toFixed(1)}M views</strong> and <strong>${creatorStats.totalRevenue.toLocaleString()} in revenue</strong>.</p>
                  <p>With an average rating of <strong>{creatorStats.avgRating}/5</strong> and <strong>{creatorStats.completionRate}% completion rate</strong>, they consistently deliver high-quality content that engages audiences effectively.</p>
                </div>
              </div>

              <div className="top-creators-platforms-section">
                <h3>Platform Presence</h3>
                <div className="top-creators-platforms-list">
                  {creatorStats.platforms.map((platform, index) => (
                    <div key={index} className="top-creators-platform-item">
                      <span className="platform-name">{platform}</span>
                      <span className="platform-status">Connected</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <div className="top-creators-content-tab">
              <div className="top-creators-content-section">
                <h3>Top Performing Content</h3>
                <div className="top-creators-content-grid">
                  {creatorStats.topPerformingContent.map((content, index) => (
                    <div key={index} className="top-creators-content-card">
                      <div className="top-creators-content-header">
                        <h4>{content.title}</h4>
                        <div className="top-creators-content-brief">{content.brief}</div>
                      </div>
                      <div className="top-creators-content-metrics">
                        <div className="top-creators-content-metric">
                          <span className="top-creators-content-value">{(content.views / 1000).toFixed(0)}K</span>
                          <span className="top-creators-content-label">Views</span>
                        </div>
                        <div className="top-creators-content-metric">
                          <span className="top-creators-content-value">{content.ctr}%</span>
                          <span className="top-creators-content-label">CTR</span>
                        </div>
                        <div className="top-creators-content-metric">
                          <span className="top-creators-content-value">{content.conversion}%</span>
                          <span className="top-creators-content-label">Conversion</span>
                        </div>
                        <div className="top-creators-content-metric">
                          <span className="top-creators-content-value">{content.rating}</span>
                          <span className="top-creators-content-label">Rating</span>
                        </div>
                      </div>
                      <div className="top-creators-content-date">
                        Created: {content.date}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="top-creators-briefs-section">
                <h3>Recent Briefs</h3>
                <div className="top-creators-briefs-list">
                  {creatorStats.recentBriefs.map((brief, index) => (
                    <div key={index} className={`top-creators-brief-item ${brief.status}`}>
                      <div className="top-creators-brief-info">
                        <h4>{brief.title}</h4>
                        <div className="top-creators-brief-status">
                          <span className={`status-badge ${brief.status}`}>
                            {brief.status === 'completed' ? 'Completed' : 
                             brief.status === 'in_progress' ? 'In Progress' : 'Pending'}
                          </span>
                          <span className={`performance-badge ${brief.performance}`}>
                            {brief.performance === 'excellent' ? 'Excellent' :
                             brief.performance === 'good' ? 'Good' : 'Pending'}
                          </span>
                        </div>
                      </div>
                      <div className="top-creators-brief-stats">
                        <div className="top-creators-brief-stat">
                          <span className="top-creators-brief-value">{(brief.views / 1000).toFixed(0)}K</span>
                          <span className="top-creators-brief-label">Views</span>
                        </div>
                        <div className="top-creators-brief-stat">
                          <span className="top-creators-brief-value">${brief.revenue.toLocaleString()}</span>
                          <span className="top-creators-brief-label">Revenue</span>
                        </div>
                        <div className="top-creators-brief-stat">
                          <span className="top-creators-brief-value">{brief.rating}</span>
                          <span className="top-creators-brief-label">Rating</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'insights' && (
            <div className="top-creators-insights-tab">
              <div className="top-creators-insights-section">
                <h3>Creator Performance Insights</h3>
                <div className="top-creators-insights-grid">
                  {creatorInsights.map((insight, index) => (
                    <div key={index} className={`top-creators-insight-card ${insight.type}`}>
                      <div className="top-creators-insight-header">
                        <div className="top-creators-insight-icon">
                          {insight.type === 'positive' && <CheckCircle size={16} />}
                          {insight.type === 'improvement' && <Target size={16} />}
                          {insight.type === 'warning' && <XCircle size={16} />}
                        </div>
                        <h4>{insight.title}</h4>
                      </div>
                      <p className="top-creators-insight-text">{insight.insight}</p>
                      <div className="top-creators-insight-recommendation">
                        <strong>Recommendation:</strong> {insight.recommendation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="top-creators-analysis-section">
                <div className="top-creators-strengths-section">
                  <h3>Creator Strengths</h3>
                  <div className="top-creators-strengths-list">
                    {creatorStats.creatorStrengths.map((strength, index) => (
                      <div key={index} className="top-creators-strength-item">
                        <CheckCircle size={16} />
                        <span>{strength}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="top-creators-improvements-section">
                  <h3>Areas for Improvement</h3>
                  <div className="top-creators-improvements-list">
                    {creatorStats.improvementAreas.map((area, index) => (
                      <div key={index} className="top-creators-improvement-item">
                        <Target size={16} />
                        <span>{area}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopCreatorsModal;
